import { useEffect, useRef, useCallback, useState } from 'react'

type PollingOptions<T> = {
  fetcher: () => Promise<T>
  interval?: number
  enabled?: boolean
  onSuccess?: (data: T) => void
  onError?: (error: Error) => void
  stopCondition?: (data: T) => boolean
}

type PollingState = {
  isPolling: boolean
  pollCount: number
}

type PollingControls = {
  startPolling: () => void
  stopPolling: () => void
  refetch: () => Promise<void>
}

export function usePolling<T>({
  fetcher,
  interval = 3000,
  enabled = false,
  onSuccess,
  onError,
  stopCondition,
}: PollingOptions<T>): PollingState & PollingControls {
  const [isPolling, setIsPolling] = useState(false)
  const [pollCount, setPollCount] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isMountedRef = useRef(true)

  const clearPollingInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const performFetch = useCallback(async () => {
    try {
      const data = await fetcher()

      if (!isMountedRef.current) return

      onSuccess?.(data)
      setPollCount((prev) => prev + 1)

      if (stopCondition?.(data)) {
        clearPollingInterval()
        setIsPolling(false)
      }
    } catch (error) {
      if (!isMountedRef.current) return
      onError?.(error as Error)
    }
  }, [fetcher, onSuccess, onError, stopCondition, clearPollingInterval])

  const startPolling = useCallback(() => {
    if (intervalRef.current) return

    setIsPolling(true)
    setPollCount(0)

    performFetch()

    intervalRef.current = setInterval(performFetch, interval)
  }, [performFetch, interval])

  const stopPolling = useCallback(() => {
    clearPollingInterval()
    setIsPolling(false)
  }, [clearPollingInterval])

  const refetch = useCallback(async () => {
    await performFetch()
  }, [performFetch])

  useEffect(() => {
    if (enabled) {
      startPolling()
    } else {
      stopPolling()
    }
  }, [enabled, startPolling, stopPolling])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      clearPollingInterval()
    }
  }, [clearPollingInterval])

  return {
    isPolling,
    pollCount,
    startPolling,
    stopPolling,
    refetch,
  }
}

export function hasActiveStatus<T extends { status: string }>(
  items: T[],
  activeStatuses: string[] = ['pending', 'running']
): boolean {
  return items.some((item) => activeStatuses.includes(item.status))
}
